import z from 'zod';
import {epochTimestamp} from '../_other';
import {tradeWriteSchema} from '../trade/tradeWriteSchema';
import {tradeStatusSchema} from '../trade/TradeStatus';
import {tradeExtensionMessageRequestSchema} from './tradeExtensionMessageRequestSchema';
import {
  refineTradeExtensionUpdateCloseStatus,
  refineTradeExtensionUpdateExitPairing,
} from './tradeExtensionSchemaRefinements';

/**
 * Patch fields must **not** reuse `tradeWriteSchema.shape.exitPrice` / `closedAt` / `profitLoss` with `.optional()`:
 * those shapes include `.default(0)`, so omitted keys parse as `0` and close refinements treat the payload as a
 * close patch (breaking message-only updates e.g. SL/TP notes).
 */
const tradeExtensionUpdatePatchSchema = z.object({
  sessionId: tradeWriteSchema.shape.sessionId,
  tradeId: tradeWriteSchema.shape.tradeId,

  exitPrice: z.number().min(0).optional(),
  closedAt: epochTimestamp.optional(),
  profitLoss: z.number().optional(),
  status: tradeStatusSchema.optional(),

  tags: tradeWriteSchema.shape.tags.default([]),
  removeTags: z.array(z.string()).default([]),

  messages: tradeExtensionMessageRequestSchema.array().default([]),
});

/**
 * Extension trade **update** — resolve the trade by `sessionId` + `tradeId` (same id allocation as create).
 * Append `messages` (note / optional screenshot each); optionally patch tags, status, prices, timestamps (e.g. close).
 */
export const tradeExtensionUpdateRequestSchema = tradeExtensionUpdatePatchSchema
  .extend({
    messages: tradeExtensionMessageRequestSchema.array().default([]),
    removeTags: z.array(z.string()).default([]),
  })
  .superRefine(refineTradeExtensionUpdateExitPairing)
  .superRefine(refineTradeExtensionUpdateCloseStatus);

export interface TradeExtensionUpdateRequest extends z.infer<typeof tradeExtensionUpdateRequestSchema> {}
